import { useCallback, useEffect, useState } from "react";
import { UserPlus, Mail, Pause, Play, Trash2, CreditCard, Award, Users } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";

const LsoBadge = ({ lso }) => {
  if (!lso || lso.status === "not_started") return <span className="text-[11px] text-neutral-500">LSO not started</span>;
  if (lso.has_certificate || lso.certificate_number) return <span className="flex items-center gap-1 text-[11px] font-bold text-[var(--accent)]"><Award size={12} /> LSO certified</span>;
  return <span className="text-[11px] text-[#F5A623]">LSO in progress{lso.modules_passed != null ? ` · ${lso.modules_passed} modules` : ""}</span>;
};

export default function CustomerTeam() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ full_name: "", email: "", title: "" });
  const [busy, setBusy] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api("/customer-team/members").then(setData).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy("add"); setErr(null);
    try {
      const r = await api("/customer-team/members", { method: "POST", body: { full_name: f.full_name.trim(), email: f.email.trim(), title: f.title.trim() || null } });
      if (r?.setup_link_sent === false) toast.warning(`Added ${f.full_name.trim()}, but the invite email didn't send. Use the envelope button to resend.`);
      else toast.success(`Invite sent to ${f.email.trim()} — they'll set their own password.`);
      setF({ full_name: "", email: "", title: "" });
      await load();
    } catch (e2) { setErr(e2.message); } finally { setBusy(null); }
  };
  const act = async (key, fn, ok) => { setBusy(key); try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(null); } };
  const buySeat = async () => {
    setBusy("seat");
    try { const r = await api("/customer-team/purchase-seat", { method: "POST" }); setInvoice(r.invoice); toast.success("Invoice created — it's been emailed to you."); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const quota = data?.quota;
  return (
    <PortalPage eyebrow="MACHINE OWNER" title="My Team" testId="customer-team-page">
      {!data && !err && <Spinner />}
      <ErrorNote>{err}</ErrorNote>
      {quota && (
        <Card className="flex flex-wrap items-center gap-4" data-testid="seat-quota">
          <Users size={26} className="text-[var(--accent)]" />
          <div className="flex-1">
            <p className="font-bold" data-testid="seat-remaining">{quota.seats_remaining} training seat{quota.seats_remaining === 1 ? "" : "s"} left</p>
            <p className="text-xs text-neutral-400">{quota.seats_used} used of {quota.seats_capacity} ({quota.seats_included} included + {quota.seats_paid} purchased). Each team member needs a seat for LSO training.</p>
          </div>
          <Btn className="sm:!w-auto" variant="outline" busy={busy === "seat"} onClick={buySeat} data-testid="buy-seat-btn"><CreditCard size={16} /> Buy a seat · $30</Btn>
          {invoice?.hosted_pay_link && <Btn onClick={() => window.open(invoice.hosted_pay_link, "_blank")} data-testid="seat-pay-link">Pay invoice #{invoice.qbo_invoice_number}</Btn>}
        </Card>
      )}
      {data && (
        <>
          <Card className="p-4" data-testid="team-owner">
            <p className="text-[10px] font-bold tracking-[0.2em] text-[var(--accent)]">ACCOUNT OWNER</p>
            <p className="font-semibold">{data.owner.full_name}</p><p className="text-xs text-neutral-400">{data.owner.email}</p><LsoBadge lso={data.owner.lso} />
          </Card>
          <Card>
            <form onSubmit={add} className="grid gap-3 sm:grid-cols-3" data-testid="team-add-form">
              <input data-testid="team-name" className={inputCls} placeholder="Full name *" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
              <input data-testid="team-email" type="email" className={inputCls} placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <input data-testid="team-title" className={inputCls} placeholder="Title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
              <div className="sm:col-span-3"><Btn type="submit" busy={busy === "add"} disabled={f.full_name.trim().length < 2 || !f.email.includes("@")} data-testid="team-add"><UserPlus size={16} /> Add team member</Btn></div>
            </form>
          </Card>
          <h2 className="pt-2 text-[11px] font-bold tracking-[0.3em] text-neutral-400">TEAM · {data.active_count} ACTIVE</h2>
          {data.members.length === 0 && <p className="text-sm text-neutral-500" data-testid="team-empty">No team members yet.</p>}
          {data.members.map((m) => (
            <Card key={m.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`team-member-${m.id}`}>
              <div className="min-w-[180px] flex-1">
                <p className="font-semibold">{m.full_name} {!m.is_active && <span className="ml-1 rounded bg-[#FF3333]/15 px-1.5 text-[10px] font-bold text-[#FF3333]">SUSPENDED</span>}</p>
                <p className="text-xs text-neutral-400">{m.email}{m.password_setup_pending ? " · invite pending" : ""}</p>
                <LsoBadge lso={m.lso} />
              </div>
              <div className="flex gap-1">
                {m.password_setup_pending && <button title="Resend invite" onClick={() => act(`link-${m.id}`, () => api(`/customer-team/members/${m.id}/send-setup-link`, { method: "POST" }), "Invite re-sent")} data-testid={`team-resend-${m.id}`} className="rounded-lg border border-[#333] p-2 hover:text-[var(--accent)]"><Mail size={16} /></button>}
                <button title={m.is_active ? "Suspend" : "Reactivate"} onClick={() => act(`sus-${m.id}`, () => api(`/customer-team/members/${m.id}/suspend`, { method: "POST", body: { is_active: !m.is_active } }))} data-testid={`team-suspend-${m.id}`} className="rounded-lg border border-[#333] p-2 hover:text-[#F5A623]">{m.is_active ? <Pause size={16} /> : <Play size={16} />}</button>
                <button title="Remove" onClick={() => window.confirm(`Remove ${m.full_name}? They lose access immediately.`) && act(`del-${m.id}`, () => api(`/customer-team/members/${m.id}`, { method: "DELETE" }), "Removed")} data-testid={`team-delete-${m.id}`} className="rounded-lg border border-[#333] p-2 hover:text-[#FF3333]"><Trash2 size={16} /></button>
              </div>
            </Card>
          ))}
        </>
      )}
    </PortalPage>
  );
}
